import {inject, Injectable} from '@angular/core';
import {TileLayerService} from '@6grain/masofa-monolith-api-client/map';
import {Observable, of} from 'rxjs';
import {map, tap} from 'rxjs/operators';
import {FilterOperator} from '@app/utils/filter-operator';
import {DataTransformer} from '@app/utils/data-transformer';
import {
  IndexType,
  SatelliteIndicesService
} from '@services/satellite-indices.service';
import {environment} from '@root/src/environments/environment';
import {OSM_OVERLAY_TILE_LAYER_OPTIONS} from '../constants/osm-tile-layer-performance.util';

@Injectable({
  providedIn: 'root'
})
export class IndexManager {
  private tileLayerService = inject(TileLayerService);
  private satelliteService = inject(SatelliteIndicesService);
  private indexTileLayer: any = null;
  private mapInstance: any = null;

  /**
   * Last opacity % (0–100) for the global index tile layer, kept on the singleton so
   * Leaflet rebuilds / zoom / extra-layer scans do not silently revert to a hard-coded default.
   */
  private indexTileOpacityPercent = 60;

  getIndexTileOpacityPercent(): number {
    return this.indexTileOpacityPercent;
  }

  /** Persist slider / API opacity before a tile layer exists or after it was removed. */
  rememberIndexTileOpacityPercent(opacityPercent: number): void {
    const n = Number(opacityPercent);
    if (!Number.isFinite(n)) {
      return;
    }
    this.indexTileOpacityPercent = Math.max(0, Math.min(100, n));
  }

  /**
   * Cache structure: { "2025-11-20": { "Arvi": "geoserver_name", "Ndvi": "geoserver_name", ... } }
   * Stores loaded index layers by date to avoid redundant API calls
   */
  private indexMapData: Record<string, Record<string, string>> = {};
  private indexAvailableYear: Record<string, string[]> = {};

  /**
   * Base URL configuration for index tiles
   */
  private readonly indexConfig = {
    name: 'Index',
    baseUrl: `${environment.api}/tiles/Map/GetTile`,
    attribution: '© 6grain'
  };

  public readonly availableIndices = [
    'Gndvi',
    'Mndwi',
    'Arvi',
    'Evi',
    'Ndmi',
    'Ndvi',
    'Ndwi',
    'Orvi',
    'Osavi',
    'Anomalies'
  ];

  /**
   * Initialize the manager with a map instance
   */
  initialize(mapInstance: any): void {
    this.mapInstance = mapInstance;
  }

  /**
   * Check if a geoServerName matches a specific index type exactly
   * Uses word boundary or separator check to avoid partial matches
   * e.g., "GNDVI" should not match "NDVI", "MNDWI" should not match "NDMI"
   */
  private matchesIndexType(geoServerName: string, indexType: string): boolean {
    if (!geoServerName || !indexType) return false;

    const index = indexType.toUpperCase();

    // // Special case for Anomalies (case-sensitive in original)
    // if (indexType === 'Anomalies') {
    //   return geoServerName.includes('Anomalies');
    // }

    // Check for exact index match with word boundaries
    // Pattern: index should be preceded by non-letter or start, and followed by non-letter or end
    const regex = new RegExp(`(^|[^A-Z])${index}([^A-Z]|$)`, 'i');
    return regex.test(geoServerName);
  }

  /**
   * Find which index type a geoServerName belongs to
   */
  private getIndexTypeFromGeoServerName(geoServerName: string): string | null {
    if (!geoServerName) return null;

    // Check in order (longer names first to avoid partial matches)
    for (const index of this.availableIndices) {
      if (this.matchesIndexType(geoServerName, index)) {
        return index;
      }
    }
    return null;
  }

  /**
   * Load available index layers for a specific date
   * Filters by geoServerName containing the index name and the formatted date
   */
  loadAvailableIndices(date: Date, indexType?: string): Observable<any[]> {
    const formattedDate = DataTransformer.formatDateToYYYYMMDD(new Date(date)) as string;
    // Check cache first

    if (this.indexMapData[formattedDate] && this.indexMapData[formattedDate][indexType as string]) {
      const cachedData = this.indexMapData[formattedDate];

      // If an index type is specified, return only the relevant layer
      if (indexType && cachedData[indexType]) {
        return new Observable(observer => {
          observer.next([{
            geoServerName: cachedData[indexType]
          }]);
          observer.complete();
        });
      }

      // Return all layers for the date from cache
      const cachedLayers = Object.keys(cachedData).map(key => ({
        geoServerName: cachedData[key]
      }));

      return new Observable(observer => {
        observer.next(cachedLayers);
        observer.complete();
      });
    }

    const filters: any[] = [
      {
        filterField: 'geoServerName',
        filterValue: formattedDate,
        filterOperator: FilterOperator.Contains
      }
    ];

    // const indexName = indexType === 'Anomalies' ? indexType : indexType?.toUpperCase();
    const indexName = indexType?.toUpperCase();
    // If a specific index type is provided, filter by it
    if (indexType) {
      filters.push({
        filterField: 'geoServerName',
        filterValue: indexName,
        filterOperator: FilterOperator.Contains
      });
    }


    return this.tileLayerService.tilesTileLayerGetByQueryPost({
      filters: filters
    }).pipe(
      map((layers: any[]) => {
        // Filter to only include layers that match our index types exactly
        const filteredLayers = layers.filter(layer => {
          const matchedIndex = this.getIndexTypeFromGeoServerName(layer.geoServerName);

          // If searching for a specific index, only return exact matches
          if (indexType) {
            return matchedIndex === indexType;
          }

          // Otherwise, return all layers that match any known index
          return matchedIndex !== null;
        });

        // Cache the loaded data by date and index type
        if (!this.indexMapData[formattedDate]) {
          this.indexMapData[formattedDate] = {};
        }

        filteredLayers.forEach(layer => {
          const matchedIndex = this.getIndexTypeFromGeoServerName(layer.geoServerName);
          if (matchedIndex) {
            this.indexMapData[formattedDate][matchedIndex] = layer.geoServerName;
          }
        });

        return filteredLayers;
      })
    );
  }

  addIndexTileLayer(indexType: string, date: Date, opacity?: number, onLoadingChange?: (loading: boolean) => void): void {
    if (!this.mapInstance) {
      console.warn('Map instance not initialized');
      return;
    }

    const resolvedOpacity =
      opacity !== undefined && opacity !== null && Number.isFinite(Number(opacity))
        ? Math.max(0, Math.min(100, Number(opacity)))
        : this.indexTileOpacityPercent;
    this.indexTileOpacityPercent = resolvedOpacity;

    // Remove existing layer first
    this.removeIndexTileLayer();

    // Show loading indicator
    if (onLoadingChange) {
      onLoadingChange(true);
    }


    // Load the specific layer from the server
    const sub = this.loadAvailableIndices(date, indexType).subscribe({
      next: (layers) => {
        if (layers.length === 0) {
          if (onLoadingChange) {
            onLoadingChange(false);
          }
          sub.unsubscribe();
          return;
        }
        // Use the first matching layer
        const layer = layers[0];
        this.createAndAddTileLayer(layer, resolvedOpacity, onLoadingChange);
      },
      error: (error) => {
        console.error('Error loading index layer:', error);
        if (onLoadingChange) {
          onLoadingChange(false);
        }
        sub.unsubscribe();
      },

    });
  }

  /**
   * Create and add the tile layer to the map
   */
  private createAndAddTileLayer(layerConfig: any, opacity: number, onLoadingChange?: (loading: boolean) => void): void {
    const baseUrl = this.indexConfig.baseUrl;

    // Create custom tile layer that converts x,y,z to lat,lon
    const IndexTileLayer = (window as any).L.TileLayer.extend({
      getTileUrl: function (coords: any) {
        const zoom = coords.z;
        const x = coords.x;
        const y = coords.y;
        const n = Math.PI - 2 * Math.PI * (y + 0.5) / Math.pow(2, zoom);
        const lat = (180 / Math.PI * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n))));
        const lon = (x + 0.5) / Math.pow(2, zoom) * 360 - 180;

        // Use the geoServerName from the layer configuration
        let layerName = layerConfig.geoServerName || layerConfig.name;
        const rgx_ind = /^([A-Za-z]+)(\d+)$/.exec(layerName);

        if (rgx_ind && rgx_ind[1]) {
          let index_name = rgx_ind[1];
          layerName = `indexint8:${layerName}`;
          return `${baseUrl}?lat=${lat}&lon=${lon}&z=${zoom}&layer=${layerName}&coordinateSystem=0&style=${index_name}_int8`;
        } else {
          return `${baseUrl}?lat=${lat}&lon=${lon}&z=${zoom}&layer=${layerName}&coordinateSystem=0`;
        }
      },
    });

    // Create and add the index tile layer with opacity
    this.indexTileLayer = new IndexTileLayer('', {
      attribution: this.indexConfig.attribution,
      opacity: opacity / 100,
      ...OSM_OVERLAY_TILE_LAYER_OPTIONS,
      className: 'index-tile-layer',
    });

    // Listen for tile loading events
    this.indexTileLayer.on('loading', () => {
      if (onLoadingChange) {
        onLoadingChange(true);
      }
    });

    this.indexTileLayer.on('load', () => {
      if (onLoadingChange) {
        onLoadingChange(false);
      }
    });

    this.indexTileLayer.on('tileerror', () => {
      // debugger
      // if (onLoadingChange) {
      //   onLoadingChange(false);
      // }
    });

    this.indexTileLayer.addTo(this.mapInstance);

    // Fallback to hide loading after a short delay
    setTimeout(() => {
      if (onLoadingChange) {
        // onLoadingChange(false);
      }
    }, 2000);
  }

  /**
   * Remove index tile layer from the map
   */
  removeIndexTileLayer(): void {
    if (this.indexTileLayer && this.mapInstance) {
      this.mapInstance.removeLayer(this.indexTileLayer);
      this.indexTileLayer = null;
    }
  }

  setCurrentLayerOpacity(opacityPercent: number, persistMemory = true): void {
    const pct = Math.max(0, Math.min(100, Number(opacityPercent) || 0));
    if (persistMemory) {
      this.indexTileOpacityPercent = pct;
    }
    if (!this.indexTileLayer?.setOpacity) {
      return;
    }
    this.indexTileLayer.setOpacity(pct / 100);
  }

  bringCurrentLayerToFront(): void {
    if (this.indexTileLayer?.bringToFront) {
      this.indexTileLayer.bringToFront();
    }
  }

  GetIndexDatesFromTileLayers(indexType: IndexType | 'Anomalies', year: number): Observable<string[]> {
    const recordKey = year + indexType
    if (this.indexAvailableYear[recordKey]) {
      return of(this.indexAvailableYear[recordKey]);
    }


    return this.satelliteService.GetIndexDatesFromTileLayers(indexType as IndexType, {
      year,
      IndexName: indexType.toUpperCase()
    }).pipe(
      tap((dates: string[]) => {
        this.indexAvailableYear[recordKey] = dates
      })
    );
  }
}
